// Context Provider dla zarządzania stanem noclegów na pełny ekran

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import {
  GuestStructure,
  AssignedGuestsSummary,
  AssignedRoom,
  RoomSelection,
  // AccommodationState,
  DisplayOptions,
  AssignmentType,
} from "./types/accommodation.types";
import {
  MarkupSettings,
  PricingModifier,
  PriceViewMode,
} from "./types/pricing.types";
import type { BatchAvailabilityResponse, PricingStrategy, DayPlanItem, CategoryOptionDto } from "../../types";
import { AccommodationHelpers } from "./utils/accommodationHelpers";
import {
  MarkupRecalculationSummary,
  planMarkupRecalculation,
} from "./utils/markupRecalculation";
import { calculateWeekendPricingSuggestion } from "../../utils/weekendPricingCalculations";
import { shouldOfferWeekendStrategy } from "../../utils/markupCalculations";
import {
  getBatchAvailability,
  updateDayAccommodation,
  createAccommodationFullStay,
  addDayPlanItem,
  deleteDayPlanItem,
  updateDayPlanItem,
  getOfferDetails,
  getPricingStrategies,
  updateAccommodationMarkup,
  getCategoryOptionsByType,
} from "../../services/api";

// Typy dla Context API
interface AccommodationContextType {
  // Stan podstawowy
  offerId: string;
  currentDayIndex: number;
  addToAllDays: boolean;

  // Dane Profitroom
  availabilityData: BatchAvailabilityResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  lastRefreshed: Date | null;

  // Struktura gości
  guestStructure: GuestStructure;
  assignedGuests: AssignedGuestsSummary;

  // Przypisane pokoje
  assignedRooms: AssignedRoom[];
  accommodationDays: any[]; // OfferDay[] z istniejącego typu

  // Narzuty i modyfikatory
  markup: MarkupSettings | null;
  pricingModifiers: PricingModifier[];

  // Opcje wyświetlania
  viewMode: PriceViewMode;
  displayOptions: DisplayOptions;

  // Strategia weekendowa
  pricingStrategies: PricingStrategy[];
  activeWeekendStrategy: PricingStrategy | null;
  dismissedStrategies: string[];

  // Akcje
  setCurrentDay: (dayIndex: number) => void;
  toggleAddToAllDays: () => void;
  updateGuestStructure: (structure: GuestStructure) => void;
  addRoom: (selection: RoomSelection) => Promise<void>;
  removeRoom: (roomId: string) => Promise<void>;
  updateRoomPrice: (roomId: string, price: number) => Promise<void>;
  updateRoomQuantity: (roomId: string, quantity: number) => Promise<void>;
  refreshAvailability: () => Promise<void>;
  /** Refreshes offer days from server and updates provider state. Used after mutations in AccommodationDetailSection. */
  refreshOfferDays: () => Promise<void>;
  /**
   * Saves the markup settings and recalculates rooms already in the offer
   * (manual prices and prices that do not match the current rates are skipped).
   */
  applyMarkup: (
    markup: MarkupSettings | null
  ) => Promise<MarkupRecalculationSummary>;
  setViewMode: (mode: PriceViewMode) => void;
  toggleDisplayOption: (option: keyof DisplayOptions) => void;
  toggleGroupCollapse: (groupType: AssignmentType) => void;
  dismissWeekendStrategy: (strategyType: string) => void;
  applyWeekendStrategy: (strategy: PricingStrategy) => Promise<void>;
  updateAccommodationItem: (dayId: string, itemId: string, updates: Partial<Pick<DayPlanItem, 'unitPrice' | 'quantity' | 'discountType' | 'discountValue'>>) => Promise<void>;
  deleteAccommodationItem: (dayId: string, itemId: string) => Promise<void>;
  /**
   * Adds an accommodation item (extra bed, or main room with occupancy type) to a day plan.
   * Extended scope: handles both dostawki and main accommodation items with occupancyType.
   */
  addAccommodationExtra: (dayId: string, extra: { categoryOptionId: string; quantity: number; unitPrice: number; vatRate: number; occupancyType?: AssignmentType; personsCount?: number }) => Promise<void>;
  getAccommodationOptions: () => Promise<CategoryOptionDto[]>;
}

// Stan dla reducer
interface AccommodationContextState {
  offerId: string;
  currentDayIndex: number;
  addToAllDays: boolean;
  availabilityData: BatchAvailabilityResponse | null;
  isLoading: boolean;
  isRefreshing: boolean;
  lastRefreshed: Date | null;
  // NOWA STRUKTURA PER-DZIEŃ dla struktury gości
  guestStructurePerDay: { [dayId: string]: GuestStructure };
  // ZACHOWANE dla kompatybilności wstecznej - computed property z aktualnego dnia
  guestStructure: GuestStructure;
  assignedGuests: AssignedGuestsSummary;
  // NOWA STRUKTURA PER-DZIEŃ dla przypisanych pokoi
  assignedRoomsPerDay: { [dayId: string]: AssignedRoom[] };
  // ZACHOWANE dla kompatybilności wstecznej - computed property z aktualnego dnia
  assignedRooms: AssignedRoom[];
  accommodationDays: any[];
  markup: MarkupSettings | null;
  pricingModifiers: PricingModifier[];
  viewMode: PriceViewMode;
  displayOptions: DisplayOptions;
  // Strategia weekendowa
  pricingStrategies: PricingStrategy[];
  activeWeekendStrategy: PricingStrategy | null;
  dismissedStrategies: string[];
  error: string | null;
}

// Akcje dla reducer
type AccommodationAction =
  | { type: "SET_CURRENT_DAY"; payload: number }
  | { type: "TOGGLE_ADD_TO_ALL_DAYS" }
  | { type: "SET_ADD_TO_ALL_DAYS"; payload: boolean }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_REFRESHING"; payload: boolean }
  | { type: "SET_AVAILABILITY_DATA"; payload: BatchAvailabilityResponse }
  | { type: "SET_GUEST_STRUCTURE"; payload: GuestStructure }
  | {
      type: "SET_GUEST_STRUCTURE_FOR_DAY";
      payload: { dayId: string; structure: GuestStructure };
    }
  | { type: "SET_GUEST_STRUCTURE_FOR_ALL_DAYS"; payload: GuestStructure }
  | {
      type: "INITIALIZE_GUEST_STRUCTURES";
      payload: { [dayId: string]: GuestStructure };
    }
  | { type: "SET_ASSIGNED_GUESTS"; payload: AssignedGuestsSummary }
  | { type: "SET_ASSIGNED_ROOMS"; payload: AssignedRoom[] }
  | {
      type: "SET_ASSIGNED_ROOMS_FOR_DAY";
      payload: { dayId: string; rooms: AssignedRoom[] };
    }
  | {
      type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS";
      payload: { [dayId: string]: AssignedRoom[] };
    }
  | {
      type: "INITIALIZE_ASSIGNED_ROOMS";
      payload: { [dayId: string]: AssignedRoom[] };
    }
  | { type: "SET_ACCOMMODATION_DAYS"; payload: any[] }
  | { type: "SET_MARKUP"; payload: MarkupSettings | null }
  | { type: "ADD_PRICING_MODIFIER"; payload: PricingModifier }
  | { type: "SET_VIEW_MODE"; payload: PriceViewMode }
  | { type: "TOGGLE_DISPLAY_OPTION"; payload: keyof DisplayOptions }
  | { type: "TOGGLE_GROUP_COLLAPSE"; payload: AssignmentType }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "REFRESH_COMPLETED"; payload: Date }
  | { type: "SET_PRICING_STRATEGIES"; payload: PricingStrategy[] }
  | { type: "SET_ACTIVE_WEEKEND_STRATEGY"; payload: PricingStrategy | null }
  | { type: "DISMISS_STRATEGY"; payload: string };

// Reducer
function accommodationReducer(
  state: AccommodationContextState,
  action: AccommodationAction
): AccommodationContextState {
  switch (action.type) {
    case "SET_CURRENT_DAY": {
      const newCurrentDayIndex = action.payload;
      const newCurrentDayId = state.accommodationDays[newCurrentDayIndex]?.id;

      // Load guest structure for the selected day
      const currentGuestStructure = state.guestStructurePerDay[
        newCurrentDayId
      ] || {
        pairs: 0,
        singles: 0,
        singlesAlone: 0,
      };

      // Load assigned rooms for the selected day
      const currentAssignedRooms =
        state.assignedRoomsPerDay[newCurrentDayId] || [];

      // Recalculate assigned guests based on current day rooms
      const newAssignedGuests =
        AccommodationHelpers.calculateAssignedGuests(currentAssignedRooms);
      newAssignedGuests.totalRequired =
        AccommodationHelpers.calculateRequiredGuests(currentGuestStructure);

      return {
        ...state,
        currentDayIndex: newCurrentDayIndex,
        guestStructure: currentGuestStructure, // Update for current day
        assignedRooms: currentAssignedRooms, // Update for current day
        assignedGuests: newAssignedGuests, // Update based on current day data
      };
    }

    case "TOGGLE_ADD_TO_ALL_DAYS":
      return { ...state, addToAllDays: !state.addToAllDays };

    case "SET_ADD_TO_ALL_DAYS":
      return { ...state, addToAllDays: action.payload };

    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "SET_REFRESHING":
      return { ...state, isRefreshing: action.payload };

    case "SET_AVAILABILITY_DATA":
      return { ...state, availabilityData: action.payload };

    case "SET_GUEST_STRUCTURE":
      const newAssignedGuests = AccommodationHelpers.calculateAssignedGuests(
        state.assignedRooms
      );
      newAssignedGuests.totalRequired =
        AccommodationHelpers.calculateRequiredGuests(action.payload);

      return {
        ...state,
        guestStructure: action.payload,
        assignedGuests: newAssignedGuests,
      };

    case "SET_GUEST_STRUCTURE_FOR_DAY": {
      const { dayId, structure } = action.payload;
      const newStructurePerDay = {
        ...state.guestStructurePerDay,
        [dayId]: structure,
      };

      // Compute current day structure for backward compatibility
      const currentDayId = state.accommodationDays[state.currentDayIndex]?.id;
      const currentGuestStructure =
        newStructurePerDay[currentDayId] || state.guestStructure;

      return {
        ...state,
        guestStructurePerDay: newStructurePerDay,
        guestStructure: currentGuestStructure, // For backward compatibility
      };
    }

    case "SET_GUEST_STRUCTURE_FOR_ALL_DAYS": {
      const newStructurePerDay: { [dayId: string]: GuestStructure } = {};
      state.accommodationDays.slice(0, -1).forEach((day) => {
        newStructurePerDay[day.id] = action.payload;
      });

      return {
        ...state,
        guestStructurePerDay: newStructurePerDay,
        guestStructure: action.payload,
      };
    }

    case "INITIALIZE_GUEST_STRUCTURES": {
      const guestStructurePerDay = action.payload;

      // Set current day's structure as the main structure for compatibility
      const currentDayId = state.accommodationDays[state.currentDayIndex]?.id;
      const currentGuestStructure =
        guestStructurePerDay[currentDayId] || state.guestStructure;

      return {
        ...state,
        guestStructurePerDay,
        guestStructure: currentGuestStructure,
      };
    }

    case "SET_ASSIGNED_GUESTS":
      return { ...state, assignedGuests: action.payload };

    case "SET_ASSIGNED_ROOMS":
      const assignedGuests = AccommodationHelpers.calculateAssignedGuests(
        action.payload
      );
      assignedGuests.totalRequired =
        AccommodationHelpers.calculateRequiredGuests(state.guestStructure);

      return {
        ...state,
        assignedRooms: action.payload,
        assignedGuests,
      };

    case "SET_ACCOMMODATION_DAYS":
      return { ...state, accommodationDays: action.payload };

    case "SET_MARKUP":
      return { ...state, markup: action.payload };

    case "ADD_PRICING_MODIFIER":
      return {
        ...state,
        pricingModifiers: [...state.pricingModifiers, action.payload],
      };

    case "SET_VIEW_MODE":
      return { ...state, viewMode: action.payload };

    case "TOGGLE_DISPLAY_OPTION":
      return {
        ...state,
        displayOptions: {
          ...state.displayOptions,
          [action.payload]: !state.displayOptions[action.payload],
        },
      };

    case "TOGGLE_GROUP_COLLAPSE":
      const newCollapsedGroups = new Set(state.displayOptions.collapsedGroups);
      if (newCollapsedGroups.has(action.payload)) {
        newCollapsedGroups.delete(action.payload);
      } else {
        newCollapsedGroups.add(action.payload);
      }

      return {
        ...state,
        displayOptions: {
          ...state.displayOptions,
          collapsedGroups: newCollapsedGroups,
        },
      };

    case "SET_ASSIGNED_ROOMS_FOR_DAY": {
      const { dayId, rooms } = action.payload;
      const newRoomsPerDay = {
        ...state.assignedRoomsPerDay,
        [dayId]: rooms,
      };

      // Compute current day rooms for backward compatibility
      const currentDayId = state.accommodationDays[state.currentDayIndex]?.id;
      const currentAssignedRooms =
        newRoomsPerDay[currentDayId] || state.assignedRooms;

      // Recalculate assigned guests based on current rooms
      const newAssignedGuests =
        AccommodationHelpers.calculateAssignedGuests(currentAssignedRooms);
      newAssignedGuests.totalRequired =
        AccommodationHelpers.calculateRequiredGuests(state.guestStructure);

      return {
        ...state,
        assignedRoomsPerDay: newRoomsPerDay,
        assignedRooms: currentAssignedRooms,
        assignedGuests: newAssignedGuests,
      };
    }

    case "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS": {
      const assignedRoomsPerDay = action.payload;
      const currentDayId = state.accommodationDays[state.currentDayIndex]?.id;
      const currentAssignedRooms = assignedRoomsPerDay[currentDayId] || [];

      // Recalculate assigned guests based on current rooms
      const newAssignedGuests =
        AccommodationHelpers.calculateAssignedGuests(currentAssignedRooms);
      newAssignedGuests.totalRequired =
        AccommodationHelpers.calculateRequiredGuests(state.guestStructure);

      return {
        ...state,
        assignedRoomsPerDay,
        assignedRooms: currentAssignedRooms,
        assignedGuests: newAssignedGuests,
      };
    }

    case "INITIALIZE_ASSIGNED_ROOMS": {
      const assignedRoomsPerDay = action.payload;
      const currentDayId = state.accommodationDays[state.currentDayIndex]?.id;
      const currentAssignedRooms = assignedRoomsPerDay[currentDayId] || [];

      // Recalculate assigned guests based on current rooms
      const newAssignedGuests =
        AccommodationHelpers.calculateAssignedGuests(currentAssignedRooms);
      newAssignedGuests.totalRequired =
        AccommodationHelpers.calculateRequiredGuests(state.guestStructure);

      return {
        ...state,
        assignedRoomsPerDay,
        assignedRooms: currentAssignedRooms,
        assignedGuests: newAssignedGuests,
      };
    }

    case "SET_ERROR":
      return { ...state, error: action.payload };

    case "REFRESH_COMPLETED":
      return { ...state, lastRefreshed: action.payload };

    case "SET_PRICING_STRATEGIES":
      return { ...state, pricingStrategies: action.payload };

    case "SET_ACTIVE_WEEKEND_STRATEGY":
      console.log("Reducer: Setting activeWeekendStrategy to:", action.payload);
      return { ...state, activeWeekendStrategy: action.payload };

    case "DISMISS_STRATEGY":
      return {
        ...state,
        dismissedStrategies: [...state.dismissedStrategies, action.payload],
        activeWeekendStrategy: null,
      };

    default:
      return state;
  }
}

// Context
const AccommodationContext = createContext<AccommodationContextType | null>(
  null
);

// Props dla Provider
interface AccommodationProviderProps {
  children: ReactNode;
  offerId: string;
  initialData?: {
    accommodationDays?: any[];
    guestStructure?: GuestStructure;
    markup?: MarkupSettings;
  };
  onOfferDaysChanged?: (offerDays: any[]) => void;
}

// Provider Component
export const AccommodationProvider: React.FC<AccommodationProviderProps> = ({
  children,
  offerId,
  initialData,
  onOfferDaysChanged,
}) => {
  // Inicjalny stan
  const initialState: AccommodationContextState = {
    offerId,
    currentDayIndex: 0,
    addToAllDays: true, // Domyślnie zaznaczone
    availabilityData: null,
    isLoading: false,
    isRefreshing: false,
    lastRefreshed: null,
    // NOWA STRUKTURA PER-DZIEŃ dla struktury gości
    guestStructurePerDay: {},
    // ZACHOWANE dla kompatybilności
    guestStructure: initialData?.guestStructure || {
      pairs: 0,
      singles: 0,
      singlesAlone: 0,
    },
    assignedGuests: {
      pairs: 0,
      singlesShared: 0,
      singlesAlone: 0,
      totalAssigned: 0,
      totalRequired: 0,
    },
    // NOWA STRUKTURA PER-DZIEŃ dla przypisanych pokoi
    assignedRoomsPerDay: {},
    // ZACHOWANE dla kompatybilności
    assignedRooms: [],
    accommodationDays: initialData?.accommodationDays || [],
    markup: initialData?.markup || null,
    pricingModifiers: [],
    viewMode: "availability",
    displayOptions: {
      showUnavailable: false, // Domyślnie ukryte
      groupAssignments: true, // Domyślnie grupowanie
      collapsedGroups: new Set(), // Żadne grupy nie są zwinięte początkowo
    },
    // Strategia weekendowa
    pricingStrategies: [],
    activeWeekendStrategy: null,
    dismissedStrategies: [],
    error: null,
  };

  const [state, dispatch] = useReducer(accommodationReducer, initialState);

  // Pobieranie strategii weekendowych przy odświeżaniu dostępności
  useEffect(() => {
    const loadWeekendStrategies = async () => {
      console.log("Loading weekend strategies:", {
        hasAvailabilityData: !!state.availabilityData,
        accommodationDaysLength: state.accommodationDays.length,
        hasMarkup: !!state.markup,
        markupValue: state.markup?.accommodationMarkupValue,
        markupType: state.markup?.accommodationMarkupType,
        lastRefreshed: state.lastRefreshed,
        dismissedStrategiesCount: state.dismissedStrategies.length,
      });

      // Sprawdź czy mamy dane potrzebne do pobrania strategii
      if (!state.availabilityData || !state.accommodationDays.length) {
        console.log("Missing required data for pricing strategies");
        return;
      }

      // Narzut albo dopłata weekendowa różne od 0 wyłączają strategię - bez żądania o strategie
      if (!shouldOfferWeekendStrategy(state.markup)) {
        return;
      }

      console.log("Markup condition passed, proceeding with strategy loading");

      try {
        const firstDay = state.accommodationDays[0];
        const lastDay =
          state.accommodationDays[state.accommodationDays.length - 1];

        if (!firstDay || !lastDay) {
          console.log("Missing first or last day");
          return;
        }

        console.log("Calling getPricingStrategies with:", {
          checkIn: firstDay.date,
          checkOut: lastDay.date,
        });

        const strategiesResponse = await getPricingStrategies(
          firstDay.date,
          lastDay.date
        );

        console.log("Received pricing strategies:", strategiesResponse);

        dispatch({
          type: "SET_PRICING_STRATEGIES",
          payload: strategiesResponse.strategies || [],
        });

        // Znajdź aktywną strategię weekendową (nie odrzuconą wcześniej)
        console.log(
          "Searching for active strategy in:",
          strategiesResponse.strategies
        );
        console.log("Dismissed strategies:", state.dismissedStrategies);

        const activeStrategy = strategiesResponse.strategies?.find(
          (strategy) => {
            const coefficientCheck = strategy.coefficient !== 1;
            const dismissedCheck = !state.dismissedStrategies.includes(
              strategy.type
            );
            console.log(
              `Strategy ${strategy.type}: coefficient=${strategy.coefficient}, coefficientCheck=${coefficientCheck}, dismissedCheck=${dismissedCheck}`
            );
            return coefficientCheck && dismissedCheck;
          }
        );

        console.log("Found active strategy:", activeStrategy);

        if (activeStrategy && state.availabilityData) {
          console.log(
            "Calculating pricing suggestion for strategy:",
            activeStrategy
          );

          // Oblicz pricing suggestion używając rzeczywistych cen
          const pricingSuggestion = calculateWeekendPricingSuggestion(
            state.availabilityData,
            activeStrategy.coefficient,
            firstDay.date // sobota (check-in date)
          );

          console.log("Calculated pricing suggestion:", pricingSuggestion);

          // Dodaj pricing suggestion do strategii
          const strategyWithPricing: PricingStrategy = {
            ...activeStrategy,
            pricingSuggestion: pricingSuggestion
              ? {
                  avgOriginalPrice: pricingSuggestion.avgSaturdayPrice,
                  suggestedPrice: pricingSuggestion.targetPrice,
                  discountAmount: Math.abs(
                    pricingSuggestion.avgSaturdayPrice -
                      pricingSuggestion.targetPrice
                  ),
                  discountPercent: pricingSuggestion.markupPercent,
                }
              : undefined,
          };

          console.log(
            "Dispatching SET_ACTIVE_WEEKEND_STRATEGY with pricing suggestion:",
            strategyWithPricing
          );

          dispatch({
            type: "SET_ACTIVE_WEEKEND_STRATEGY",
            payload: strategyWithPricing,
          });
        } else {
          console.log(
            "No active strategy found or missing availability data, activeWeekendStrategy will remain null"
          );
        }
      } catch (error) {
        console.error("Failed to load pricing strategies:", error);
      }
    };

    loadWeekendStrategies();
  }, [
    state.availabilityData,
    state.accommodationDays,
    state.markup,
    state.dismissedStrategies,
    state.lastRefreshed, // Dodane żeby triggerować przy każdym odświeżeniu
    offerId,
  ]);

  // Inicjalizacja struktur per-dzień z danych początkowych
  useEffect(() => {
    if (
      initialData?.accommodationDays &&
      initialData.accommodationDays.length > 0
    ) {
      // 1. Inicjalizacja struktur per-dzień
      const guestStructurePerDay: { [dayId: string]: GuestStructure } = {};

      // Załaduj strukturę z każdego dnia (wykluczając ostatni dzień)
      initialData.accommodationDays.slice(0, -1).forEach((day) => {
        guestStructurePerDay[day.id] = {
          pairs: day.pairs || 0,
          singles: day.singles || 0,
          singlesAlone: day.singlesAlone || 0,
        };
      });

      // 2. NOWE: Sprawdź czy struktury są identyczne
      const allIdentical =
        AccommodationHelpers.areAllDayStructuresIdentical(guestStructurePerDay);

      // 3. NOWE: Ustaw checkbox na unchecked jeśli struktury się różnią
      if (!allIdentical) {
        dispatch({ type: "SET_ADD_TO_ALL_DAYS", payload: false });
      }

      dispatch({
        type: "INITIALIZE_GUEST_STRUCTURES",
        payload: guestStructurePerDay,
      });

      // 4. NOWE: Wyekstraktuj przypisane pokoje per-dzień
      const assignedRoomsPerDay =
        AccommodationHelpers.extractAssignedRoomsFromDays(
          initialData.accommodationDays
        );
      dispatch({
        type: "INITIALIZE_ASSIGNED_ROOMS",
        payload: assignedRoomsPerDay,
      });
    }
  }, [initialData?.accommodationDays]);

  // Funkcje akcji
  const setCurrentDay = useCallback((dayIndex: number) => {
    dispatch({ type: "SET_CURRENT_DAY", payload: dayIndex });
  }, []);

  const toggleAddToAllDays = useCallback(() => {
    dispatch({ type: "TOGGLE_ADD_TO_ALL_DAYS" });
  }, []);

  const updateGuestStructure = useCallback(
    async (structure: GuestStructure) => {
      const accommodationDays = state.accommodationDays.slice(0, -1);

      try {
        if (state.addToAllDays) {
          // MODE 1: Checkbox ✓ - synchronizuj wszystkie dni
          dispatch({
            type: "SET_GUEST_STRUCTURE_FOR_ALL_DAYS",
            payload: structure,
          });

          // Update all accommodation days via API
          for (const day of accommodationDays) {
            await updateDayAccommodation(offerId, day.id, structure);
          }
        } else {
          // MODE 2: Checkbox ✗ - tylko aktualny dzień
          const currentDay = accommodationDays[state.currentDayIndex];
          if (currentDay) {
            dispatch({
              type: "SET_GUEST_STRUCTURE_FOR_DAY",
              payload: { dayId: currentDay.id, structure },
            });

            // Update only current day via API
            await updateDayAccommodation(offerId, currentDay.id, structure);
          }
        }
      } catch (error) {
        console.error("Failed to update guest structure:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Nie udało się zaktualizować struktury gości",
        });
      }
    },
    [
      offerId,
      state.accommodationDays,
      state.currentDayIndex,
      state.addToAllDays,
    ]
  );

  const refreshAvailability = useCallback(
    async (silent = false) => {
      if (silent) {
        dispatch({ type: "SET_REFRESHING", payload: true });
      } else {
        dispatch({ type: "SET_LOADING", payload: true });
      }
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const response = await getBatchAvailability(offerId);
        dispatch({ type: "SET_AVAILABILITY_DATA", payload: response });
        dispatch({ type: "REFRESH_COMPLETED", payload: new Date() });
      } catch (error) {
        console.error("Failed to refresh availability:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Nie udało się pobrać dostępności pokoi",
        });
      } finally {
        if (silent) {
          dispatch({ type: "SET_REFRESHING", payload: false });
        } else {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    },
    [offerId]
  );

  // Auto-load availability data na starcie
  useEffect(() => {
    if (offerId && !state.availabilityData) {
      refreshAvailability();
    }
  }, [offerId, state.availabilityData, refreshAvailability]);

  const addRoom = useCallback(
    async (selection: RoomSelection) => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const accommodationDays = state.accommodationDays.slice(0, -1); // Bez checkout dnia

        if (state.addToAllDays) {
          // Dodaj na wszystkie dni
          const firstDay = accommodationDays[0];
          await createAccommodationFullStay(offerId, firstDay.id, {
            categoryOptionId: selection.categoryOptionId,
            quantity: selection.quantity,
            unitPrice: selection.pricePerRoom,
            occupancyType: selection.assignmentType,
            dailyPrices: selection.dailyPrices,
            dailySuggestedPrices: selection.dailySuggestedPrices,
            dailyBasePrices: selection.dailyBasePrices,
            personsCount: selection.personsPerRoom,
            notes: `Dodane z nowego interfejsu`,
            timeFrom: "",
            timeTo: "",
            vatRate: 8, // Stawka VAT dla noclegów
          });
        } else {
          // Dodaj tylko na aktualny dzień
          const currentDay = accommodationDays[state.currentDayIndex];
          await addDayPlanItem(offerId, currentDay.id, {
            categoryOptionId: selection.categoryOptionId,
            quantity: selection.quantity,
            unitPrice: selection.pricePerRoom,
            occupancyType: selection.assignmentType,
            ...(selection.basePrices && {
              suggestedPrice: selection.suggestedPrice,
              profitroomPriceSingle: selection.basePrices.single,
              profitroomPriceDouble: selection.basePrices.double,
              profitroomPriceTriple: selection.basePrices.triple,
            }),
            personsCount: selection.personsPerRoom,
            notes: `Dodane z nowego interfejsu`,
            timeFrom: "",
            timeTo: "",
            vatRate: 8, // Stawka VAT dla noclegów
          });
        }

        // Odśwież dane oferty
        const refreshedOffer = await getOfferDetails(offerId);
        dispatch({
          type: "SET_ACCOMMODATION_DAYS",
          payload: refreshedOffer.offerDays,
        });
        onOfferDaysChanged?.(refreshedOffer.offerDays);

        // Wyekstraktuj przypisane pokoje z dni noclegowych per-dzień
        const assignedRoomsPerDay =
          AccommodationHelpers.extractAssignedRoomsFromDays(
            refreshedOffer.offerDays
          );
        dispatch({
          type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS",
          payload: assignedRoomsPerDay,
        });
      } catch (error) {
        console.error("Failed to add room:", error);
        dispatch({ type: "SET_ERROR", payload: "Nie udało się dodać pokoju" });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [
      offerId,
      state.addToAllDays,
      state.currentDayIndex,
      state.accommodationDays,
      onOfferDaysChanged,
    ]
  );

  const removeRoom = useCallback(
    async (roomId: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const accommodationDays = state.accommodationDays.slice(0, -1);

        if (state.addToAllDays) {
          // Usuń ze wszystkich dni
          for (const day of accommodationDays) {
            try {
              await deleteDayPlanItem(offerId, day.id, roomId);
            } catch (error: any) {
              // Ignoruj błędy 404 - pokój może nie istnieć na każdym dniu
              if (error.response?.status !== 404) {
                throw error;
              }
            }
          }
        } else {
          // Usuń tylko z aktualnego dnia
          const currentDay = accommodationDays[state.currentDayIndex];
          await deleteDayPlanItem(offerId, currentDay.id, roomId);
        }

        // Odśwież dane
        const refreshedOffer = await getOfferDetails(offerId);
        dispatch({
          type: "SET_ACCOMMODATION_DAYS",
          payload: refreshedOffer.offerDays,
        });
        onOfferDaysChanged?.(refreshedOffer.offerDays);

        const assignedRoomsPerDay =
          AccommodationHelpers.extractAssignedRoomsFromDays(
            refreshedOffer.offerDays
          );
        dispatch({
          type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS",
          payload: assignedRoomsPerDay,
        });
      } catch (error) {
        console.error("Failed to remove room:", error);
        dispatch({ type: "SET_ERROR", payload: "Nie udało się usunąć pokoju" });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [
      offerId,
      state.addToAllDays,
      state.currentDayIndex,
      state.accommodationDays,
      onOfferDaysChanged,
    ]
  );

  const updateRoomPrice = useCallback(
    async (roomId: string, price: number) => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const accommodationDays = state.accommodationDays.slice(0, -1);

        if (state.addToAllDays) {
          // Aktualizuj cenę na wszystkich dniach
          for (const day of accommodationDays) {
            // Znajdź pokój na tym dniu - priorytet: exact ID > linkedItemId > fallback
            // TODO: docelowo każde przypisanie powinno mieć unikalne ID, fallback do usunięcia
            const items = day.planItems ?? [];
            const roomOnDay =
              items.find((item: any) => item.id === roomId) ||
              items.find((item: any) => item.linkedItemId === roomId) ||
              items.find((item: any) => {
                const target = state.assignedRooms.find((r) => r.id === roomId);
                return (
                  target &&
                  item.categoryOption?.id === target.categoryOptionId &&
                  item.occupancyType === target.occupancyType
                );
              });

            if (roomOnDay) {
              // Rzeczywiste API call do aktualizacji ceny
              await updateDayPlanItem(offerId, day.id, roomOnDay.id, {
                unitPrice: price,
              });
            }
          }
        } else {
          // Aktualizuj cenę tylko na aktualnym dniu
          const currentDay = accommodationDays[state.currentDayIndex];
          const roomOnDay = currentDay.planItems?.find(
            (item: any) => item.id === roomId || item.linkedItemId === roomId
          );

          if (roomOnDay) {
            // Rzeczywiste API call do aktualizacji ceny
            await updateDayPlanItem(offerId, currentDay.id, roomOnDay.id, {
              unitPrice: price,
            });
          }
        }

        // Odśwież dane oferty
        const refreshedOffer = await getOfferDetails(offerId);
        dispatch({
          type: "SET_ACCOMMODATION_DAYS",
          payload: refreshedOffer.offerDays,
        });
        onOfferDaysChanged?.(refreshedOffer.offerDays);

        const assignedRoomsPerDay =
          AccommodationHelpers.extractAssignedRoomsFromDays(
            refreshedOffer.offerDays
          );
        dispatch({
          type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS",
          payload: assignedRoomsPerDay,
        });
      } catch (error) {
        console.error("Failed to update room price:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Nie udało się zaktualizować ceny pokoju",
        });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [
      offerId,
      state.addToAllDays,
      state.currentDayIndex,
      state.accommodationDays,
      state.assignedRooms,
      onOfferDaysChanged,
    ]
  );

  const updateRoomQuantity = useCallback(
    async (roomId: string, newQuantity: number) => {
      dispatch({ type: "SET_REFRESHING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const accommodationDays = state.accommodationDays.slice(0, -1);
        const room = state.assignedRooms.find((r) => r.id === roomId);

        if (!room) {
          throw new Error("Room not found");
        }

        // Optimistic update - aktualizuj lokalny stan
        const updatedAssignedRooms = state.assignedRooms.map((r) =>
          r.id === roomId ? { ...r, quantity: newQuantity } : r
        );
        dispatch({ type: "SET_ASSIGNED_ROOMS", payload: updatedAssignedRooms });

        if (state.addToAllDays) {
          // Aktualizuj ilość na wszystkich dniach
          for (const day of accommodationDays) {
            // Znajdź pokój na tym dniu - priorytet: exact ID > linkedItemId > fallback
            // TODO: docelowo każde przypisanie powinno mieć unikalne ID, fallback do usunięcia
            const items = day.planItems ?? [];
            const roomOnDay =
              items.find((item: any) => item.id === roomId) ||
              items.find((item: any) => item.linkedItemId === roomId) ||
              items.find(
                (item: any) =>
                  item.categoryOption?.id === room.categoryOptionId &&
                  item.occupancyType === room.occupancyType
              );

            if (roomOnDay) {
              // Rzeczywiste API call do aktualizacji plan item
              await updateDayPlanItem(offerId, day.id, roomOnDay.id, {
                quantity: newQuantity,
              });
            }
          }
        } else {
          // Aktualizuj ilość tylko na aktualnym dniu
          const currentDay = accommodationDays[state.currentDayIndex];
          const items = currentDay.planItems ?? [];
          const roomOnDay =
            items.find((item: any) => item.id === roomId) ||
            items.find((item: any) => item.linkedItemId === roomId) ||
            items.find(
              (item: any) =>
                item.categoryOption?.id === room.categoryOptionId &&
                item.occupancyType === room.occupancyType
            );

          if (roomOnDay) {
            // Rzeczywiste API call do aktualizacji plan item
            await updateDayPlanItem(offerId, currentDay.id, roomOnDay.id, {
              quantity: newQuantity,
            });
          }
        }

        // Odśwież dane oferty z serwera
        const refreshedOffer = await getOfferDetails(offerId);
        dispatch({
          type: "SET_ACCOMMODATION_DAYS",
          payload: refreshedOffer.offerDays,
        });
        onOfferDaysChanged?.(refreshedOffer.offerDays);

        const assignedRoomsPerDay =
          AccommodationHelpers.extractAssignedRoomsFromDays(
            refreshedOffer.offerDays
          );
        dispatch({
          type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS",
          payload: assignedRoomsPerDay,
        });

        // Ciche odświeżenie dostępności po zmianie ilości pokoi
        refreshAvailability(true);
      } catch (error) {
        console.error("Failed to update room quantity:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Nie udało się zaktualizować ilości pokoi",
        });
        throw error; // Re-throw dla rollback w komponencie
      } finally {
        dispatch({ type: "SET_REFRESHING", payload: false });
      }
    },
    [
      offerId,
      state.addToAllDays,
      state.currentDayIndex,
      state.accommodationDays,
      state.assignedRooms,
      refreshAvailability,
      onOfferDaysChanged,
    ]
  );

  const applyMarkup = useCallback(
    async (markup: MarkupSettings | null) => {
      try {
        // Zawsze zapisz do bazy, nawet gdy resetujemy (wartość 0)
        if (markup) {
          await updateAccommodationMarkup(offerId, {
            accommodationMarkupType: markup.accommodationMarkupType,
            accommodationMarkupValue: markup.accommodationMarkupValue,
            accommodationWeekendSurchargePercent:
              markup.weekendSurchargePercent ?? 0,
          });
        } else {
          // Resetowanie - ustaw oba procenty na 0
          await updateAccommodationMarkup(offerId, {
            accommodationMarkupType: "percentage",
            accommodationMarkupValue: 0,
            accommodationWeekendSurchargePercent: 0,
          });
        }

        dispatch({ type: "SET_MARKUP", payload: markup });

        // Przelicz pokoje już dodane do oferty (stary narzut -> nowy)
        const { updates, summary } = planMarkupRecalculation(
          state.accommodationDays,
          state.availabilityData,
          state.markup,
          markup
        );
        for (const update of updates) {
          await updateDayPlanItem(
            offerId,
            update.dayId,
            update.itemId,
            update.changes
          );
        }
        if (updates.length > 0) {
          const refreshedOffer = await getOfferDetails(offerId);
          dispatch({
            type: "SET_ACCOMMODATION_DAYS",
            payload: refreshedOffer.offerDays,
          });
          onOfferDaysChanged?.(refreshedOffer.offerDays);
          dispatch({
            type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS",
            payload: AccommodationHelpers.extractAssignedRoomsFromDays(
              refreshedOffer.offerDays
            ),
          });
        }

        // Dodaj modifier tylko gdy wartość nie jest 0
        if (markup && markup.accommodationMarkupValue !== 0) {
          dispatch({
            type: "ADD_PRICING_MODIFIER",
            payload: {
              type: "markup",
              value: markup.accommodationMarkupValue,
              isPercentage: markup.accommodationMarkupType === "percentage",
              appliedAt: new Date(),
            },
          });
        }

        return summary;
      } catch (error) {
        console.error("Failed to update markup:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Nie udało się zaktualizować narzutu",
        });
        // Przekaż błąd do okna narzutu, żeby nie zamykało się jak po sukcesie
        throw error;
      }
    },
    [
      offerId,
      state.accommodationDays,
      state.availabilityData,
      state.markup,
      onOfferDaysChanged,
    ]
  );

  const setViewMode = useCallback((mode: PriceViewMode) => {
    dispatch({ type: "SET_VIEW_MODE", payload: mode });
  }, []);

  const toggleDisplayOption = useCallback((option: keyof DisplayOptions) => {
    dispatch({ type: "TOGGLE_DISPLAY_OPTION", payload: option });
  }, []);

  const toggleGroupCollapse = useCallback((groupType: AssignmentType) => {
    dispatch({ type: "TOGGLE_GROUP_COLLAPSE", payload: groupType });
  }, []);

  /**
   * Refreshes offer days from server, updates provider state,
   * and notifies parent (OfferWizard) via onOfferDaysChanged callback.
   */
  const refreshOfferDays = useCallback(async () => {
    try {
      const refreshedOffer = await getOfferDetails(offerId);
      dispatch({
        type: "SET_ACCOMMODATION_DAYS",
        payload: refreshedOffer.offerDays,
      });
      onOfferDaysChanged?.(refreshedOffer.offerDays);

      const assignedRoomsPerDay =
        AccommodationHelpers.extractAssignedRoomsFromDays(
          refreshedOffer.offerDays
        );
      dispatch({
        type: "SET_ASSIGNED_ROOMS_FOR_ALL_DAYS",
        payload: assignedRoomsPerDay,
      });
    } catch (error) {
      console.error("Failed to refresh offer days:", error);
      dispatch({
        type: "SET_ERROR",
        payload: "Nie udało się odświeżyć danych oferty",
      });
    }
  }, [offerId, onOfferDaysChanged]);

  const updateAccommodationItem = useCallback(
    async (dayId: string, itemId: string, updates: Partial<Pick<DayPlanItem, 'unitPrice' | 'quantity' | 'discountType' | 'discountValue'>>) => {
      try {
        await updateDayPlanItem(offerId, dayId, itemId, updates);
        await refreshOfferDays();
      } catch (error) {
        console.error("Failed to update accommodation item:", error);
        dispatch({ type: "SET_ERROR", payload: "Nie udalo sie zaktualizowac pozycji noclegowej" });
        throw error;
      }
    },
    [offerId, refreshOfferDays]
  );

  const deleteAccommodationItem = useCallback(
    async (dayId: string, itemId: string) => {
      try {
        await deleteDayPlanItem(offerId, dayId, itemId);
        await refreshOfferDays();
      } catch (error) {
        console.error("Failed to delete accommodation item:", error);
        dispatch({ type: "SET_ERROR", payload: "Nie udalo sie usunac pozycji noclegowej" });
        throw error;
      }
    },
    [offerId, refreshOfferDays]
  );

  const addAccommodationExtra = useCallback(
    async (dayId: string, extra: { categoryOptionId: string; quantity: number; unitPrice: number; vatRate: number; occupancyType?: AssignmentType; personsCount?: number }) => {
      try {
        await addDayPlanItem(offerId, dayId, extra);
        await refreshOfferDays();
      } catch (error) {
        console.error("Failed to add accommodation extra:", error);
        dispatch({ type: "SET_ERROR", payload: "Nie udalo sie dodac dostawki" });
        throw error;
      }
    },
    [offerId, refreshOfferDays]
  );

  const getAccommodationOptions = useCallback(
    async (): Promise<CategoryOptionDto[]> => {
      try {
        return await getCategoryOptionsByType("accommodation");
      } catch (error) {
        console.error("Failed to fetch accommodation options:", error);
        dispatch({ type: "SET_ERROR", payload: "Nie udalo sie pobrac opcji noclegowych" });
        throw error;
      }
    },
    []
  );

  // Context value
  const contextValue: AccommodationContextType = {
    // Stan
    offerId: state.offerId,
    currentDayIndex: state.currentDayIndex,
    addToAllDays: state.addToAllDays,
    availabilityData: state.availabilityData,
    isLoading: state.isLoading,
    isRefreshing: state.isRefreshing,
    lastRefreshed: state.lastRefreshed,
    guestStructure: state.guestStructure,
    assignedGuests: state.assignedGuests,
    assignedRooms: state.assignedRooms,
    accommodationDays: state.accommodationDays,
    markup: state.markup,
    pricingModifiers: state.pricingModifiers,
    viewMode: state.viewMode,
    displayOptions: state.displayOptions,
    // Strategia weekendowa
    pricingStrategies: state.pricingStrategies,
    activeWeekendStrategy: state.activeWeekendStrategy,
    dismissedStrategies: state.dismissedStrategies,

    // Akcje
    setCurrentDay,
    toggleAddToAllDays,
    updateGuestStructure,
    addRoom,
    removeRoom,
    updateRoomPrice,
    updateRoomQuantity,
    refreshAvailability,
    refreshOfferDays,
    applyMarkup,
    setViewMode,
    toggleDisplayOption,
    toggleGroupCollapse,
    updateAccommodationItem,
    deleteAccommodationItem,
    addAccommodationExtra,
    getAccommodationOptions,
    dismissWeekendStrategy: (strategyType: string) => {
      dispatch({ type: "DISMISS_STRATEGY", payload: strategyType });
    },
    applyWeekendStrategy: async (strategy: PricingStrategy) => {
      try {
        // Oblicz wartość narzutu/rabatu na podstawie strategii
        const discountPercent = strategy.pricingSuggestion?.discountPercent
          ? strategy.pricingSuggestion.discountPercent
          : (1 - strategy.coefficient) * 100;

        // Zastosuj markup przez API
        await updateAccommodationMarkup(offerId, {
          accommodationMarkupType: "percentage",
          accommodationMarkupValue: discountPercent,
        });

        // Aktualizuj lokalny stan
        const newMarkup: MarkupSettings = {
          accommodationMarkupType: "percentage",
          accommodationMarkupValue: discountPercent,
        };

        dispatch({ type: "SET_MARKUP", payload: newMarkup });
        dispatch({ type: "SET_ACTIVE_WEEKEND_STRATEGY", payload: null });

        // Dodaj modifier do historii
        dispatch({
          type: "ADD_PRICING_MODIFIER",
          payload: {
            type: "markup",
            value: discountPercent,
            isPercentage: true,
            appliedAt: new Date(),
          },
        });
      } catch (error) {
        console.error("Failed to apply weekend strategy:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Nie udało się zastosować strategii weekendowej",
        });
      }
    },
  };

  return (
    <AccommodationContext.Provider value={contextValue}>
      {children}
    </AccommodationContext.Provider>
  );
};

// Custom hook do użycia Context
export const useAccommodation = () => {
  const context = useContext(AccommodationContext);
  if (!context) {
    throw new Error(
      "useAccommodation must be used within AccommodationProvider"
    );
  }
  return context;
};

// Export Context dla zaawansowanego użytku
export { AccommodationContext };
export type { AccommodationContextType, AccommodationContextState };
